import React, { useEffect, useState } from "react";
import classNames from "classnames";
import moment from "moment";

// Utils
import { fetchWithToken } from "../../api/fetch";
import { isColorDark } from "../../utils/colorDark";

// Icons
import { BsMessenger, BsTelegram } from "react-icons/bs";
import { FaInstagramSquare } from "react-icons/fa";
import { RiWhatsappFill } from "react-icons/ri";
import { AiFillMessage } from "react-icons/ai";

/**
 * Componente con las iniciales del agente
 *
 * @param {object} agent - Información del gente
 * @returns {JSX.Element} - Iniciales del agente
 */
export const InitialsAgent = React.memo(({ agent }) => {
  if (!agent) return null;

  // Obtiene las iniciales del agente
  const getInitials = (name) => {
    const names = name.trim().split(" ");
    if (names.length > 1 && names[1]) {
      return names[0][0] + names[1][0];
    } else {
      return names[0].substring(0, 2);
    }
  };

  return (
    <span
      className="badge border font-11 text-center align-content-center fw-bolder"
      title={agent.name}
      style={{
        backgroundColor: agent ? agent.color : "#ccc",
        color: agent && isColorDark(agent.color) ? "#fff" : "#000",
        minWidth: "22px",
        borderRadius: "50%",
        padding: "6px",
      }}
    >
      {getInitials(agent.name)}
    </span>
  );
});

/**
 * Muestra el agente asignado al chat
 *
 * @param {string} id - ID del agente
 * @returns {JSX.Element} - Estado del agente
 */
export const Agent = ({ id }) => {
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    const fetchAgent = async () => {
      setAgent(null);

      if (!id) return;

      try {
        const agentResponse = await fetchWithToken(
          `agentes/getAgente`,
          { id_agent: id },
          "PUT"
        );

        if (agentResponse.ok) setAgent(agentResponse.agente);
      } catch (error) {
        console.error("Error fetching agent:", error);
      }
    };

    fetchAgent();
  }, [id]);

  return <InitialsAgent agent={agent} />;
};

/**
 * Muestra el estado actual del chat
 * - Cerrado: El chat ha sido cerrado
 * - En espera: El chat está en espera
 * - Asignado: El chat ha sido asignado a un agente
 *
 * @param {object} user - Usuario del chat
 * @returns {JSX.Element} - Elemento con el estado del chat
 */
export const ChatState = ({ user }) => {
  return (
    <>
      {user.es === "cerrado" ? (
        <div className={`badge text-white bg-danger`} title="Cerrado">
          <i className={`uil uil-lock font-13`} />
        </div>
      ) : user.es === "espera" ? (
        <div className={`badge text-white bg-warning`} title="En espera">
          <i className={`uil uil-clock-five font-13`} />
        </div>
      ) : user.es === "asignado" ? (
        <Agent id={user.id_agente} />
      ) : null}
    </>
  );
};

/**
 * Muestra el tipo de mensaje del último mensaje en la lista de chats
 *
 * @param {object} chatItem - Elemento del ChatList
 * @param {string} chatItem.type - Tipo del mensaje
 * @param {string} chatItem.object - Contenido del mensaje
 * @returns {JSX.Element} - Elemento con el tipo de mensaje
 */
export const TypeMsg = ({ chatItem }) => {
  const typeMap = {
    text: { icon: "", text: chatItem.object },
    image: {
      icon: "uil uil-image",
      text: "Imagen",
    },
    photo: {
      icon: "uil uil-image",
      text: "Imagen",
    },
    audio: {
      icon: "uil uil-volume",
      text: "Audio",
    },
    video: { icon: "uil uil-video", text: "Video" },
    file: { icon: "uil uil-file", text: "Archivo" },
    map: {
      icon: "uil uil-location-point",
      text: "Ubicación",
    },
    contact: {
      icon: "uil uil-user",
      text: "Contacto",
    },
    sticker: {
      icon: "mdi mdi-sticker-emoji",
      text: "Sticker",
    },
    gif: { icon: "uil uil-gif", text: "GIF" },
  };

  // Obtiene el icono y el texto del tipo de mensaje
  const { icon, text } = typeMap[chatItem.type] || {
    icon: "",
    text: chatItem.type,
  };

  return (
    <>
      {icon && (
        <i className={classNames(icon, "font-16 text-secondary me-1")} />
      )}
      <span className="text-secondary">{text}</span>
    </>
  );
};

/**
 * Formatea la fecha del último mensaje en la lista de chats
 *
 * @param {string} lastMessageTime - Fecha del último mensaje
 * @returns {string} - Fecha formateada
 */
export const formatLastMessageTime = (lastMessageTime) => {
  return moment(lastMessageTime).calendar(null, {
    sameDay: "HH:mm",
    lastDay: "[Ayer]",
    lastWeek: "ddd D",
    sameElse: "DD/MM/YY",
  });
};

/**
 * Formatea la fecha del mensaje en el chat
 *
 * @param {string} messageTime - Fecha del mensaje
 * @returns {string} - Fecha formateada
 */
export const formatMessageTime = (messageTime) => {
  return moment(messageTime).calendar(null, {
    sameDay: "HH:mm",
    lastDay: "[Ayer] HH:mm",
    lastWeek: "ddd HH:mm",
    sameElse: "DD/MM/YY HH:mm",
  });
};

/**
 * Determina el tipo de archivo que se está enviando
 *
 * @param {string} type Tipo del archivo
 * @returns {string} Tipo de archivo
 */
export const typeFile = (type) => {
  return type.includes("video")
    ? "video"
    : type.includes("audio")
    ? "audio"
    : type.includes("image")
    ? "image"
    : "file";
};

/**
 * Muestra el estado del mensaje
 *
 * @param {string} status - Estado del mensaje
 * @returns {JSX.Element} Componente con el estado del mensaje
 */
export const MessageStatus = ({ status }) => {
  const statusIcons = {
    clock: { icon: "mdi mdi-clock", title: "Espera" },
    submitted: { icon: "mdi mdi-content-save", title: "Guardado" },
    enqueued: { icon: "mdi mdi-clock-outline", title: "En cola" },
    sent: { icon: "mdi mdi-check", title: "Enviado" },
    delivered: { icon: "mdi mdi-check-all", title: "Entregado" },
    read: { icon: "mdi mdi-check-all", title: "Leido" },
    failed: { icon: "mdi mdi-close", title: "Fallido" },
    deleted: { icon: "mdi mdi-delete", title: "Eliminado" },
    send: { icon: "mdi mdi-check", title: "Enviado" },
    unread: {
      icon: "mdi mdi-check-all",
      title: "Recibido",
    },
  };

  // Obtiene el icono y el título del estado del mensaje
  const { icon, title } = statusIcons[status] || "";

  return (
    <span
      className={`${status === "read" ? "text-primary" : "text-secondary"}`}
      title={title}
    >
      <i className={`${icon} font-14`} />
    </span>
  );
};

/**
 * Muestra el icono de la red social
 *
 * @param {string} network - Red social
 * @returns {JSX.Element} - Icono de la red social
 */
export const NetworkIcon = ({ network }) => {
  return (
    <>
      <div className="position-relative">
        {network === "messenger" || network === "page" ? (
          <BsMessenger
            className="position-absolute bg-light rounded-circle text-primary bottom-0"
            size={22}
            title="Messenger"
          />
        ) : network === "telegram" ? (
          <BsTelegram
            className="position-absolute bg-light rounded-circle text-info bottom-0"
            size={22}
            title="Telegram"
          />
        ) : network === "instagram" ? (
          <FaInstagramSquare
            className="position-absolute bg-light rounded-circle bottom-0"
            color="#E1306C"
            size={22}
            title="Instagram"
          />
        ) : network === "RTWApi" ? (
          <RiWhatsappFill
            className="position-absolute bg-light rounded-circle bottom-0"
            color="#25D366"
            size={22}
            title="RTWApi"
          />
        ) : network === "RWTApi" ? (
          <RiWhatsappFill
            className="position-absolute bg-light text-danger rounded-circle bottom-0"
            size={22}
            title="RWTApi"
          />
        ) : network === "RwtPrueba" ? (
          <RiWhatsappFill
            className="position-absolute bg-light text-primary rounded-circle bottom-0"
            size={22}
            title="RwtPrueba"
          />
        ) : null}
      </div>
    </>
  );
};
